import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Card } from '@/elements/shared/Card/Card';
import { PRODUCT_IMAGES } from '../mockData';
import styles from './StyleGuideCards.module.css';

const IMG = PRODUCT_IMAGES.badgeScanner;

interface StateCardProps {
  label: string;
  orientation?: 'vertical' | 'horizontal';
  selected?: boolean;
  disabled?: boolean;
  hasAlwaysOn?: boolean;
  hasCheck?: boolean;
  description?: string;
  cost?: string;
  imageSrc?: string;
  className?: string;
}

const StateCard: React.FC<StateCardProps> = ({
  label,
  orientation = 'vertical',
  selected,
  disabled,
  hasAlwaysOn,
  hasCheck,
  description,
  cost,
  imageSrc = IMG,
  className,
}) => (
  <Card
    orientation={orientation}
    label={label}
    description={description}
    cost={cost}
    imageSrc={imageSrc}
    value={label}
    selected={selected}
    disabled={disabled}
    hasAlwaysOn={hasAlwaysOn}
    hasCheck={hasCheck}
    className={className}
  />
);

/** Interactive demo — click cards to see idle↔active transition animation */
const AnimationDemo: React.FC = () => {
  const [selectedVertical, setSelectedVertical] = useState<string | null>(null);
  const [selectedHorizontal, setSelectedHorizontal] = useState<string | null>(null);

  const toggleVertical = (id: string) => setSelectedVertical((prev) => (prev === id ? null : id));
  const toggleHorizontal = (id: string) => setSelectedHorizontal((prev) => (prev === id ? null : id));

  return (
    <>
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Vertical — click to toggle</h3>
        <div className={styles.demo}>
          <div className={styles.stateGrid}>
            {['Scanner A', 'Scanner B', 'Scanner C'].map((label) => (
              <div key={label} className={styles.stateCardVerticalTall}>
                <Card
                  orientation="vertical"
                  label={label}
                  imageSrc={IMG}
                  value={label}
                  selected={selectedVertical === label}
                  hasCheck={selectedVertical === label}
                  onClick={() => toggleVertical(label)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Horizontal — click to toggle</h3>
        <div className={styles.demo}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '430px' }}>
            {['Badge Scanner', 'Registration Desk', 'Event Schedule'].map((label) => (
              <Card
                key={label}
                orientation="horizontal"
                label={label}
                imageSrc={IMG}
                value={label}
                selected={selectedHorizontal === label}
                onClick={() => toggleHorizontal(label)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Card States tab — mirrors Figma "Card / States" (node-id: 3:3227).
 *
 * Shows all interactive states for vertical and horizontal cards.
 * Extras: design tokens table, click-active animation specs.
 */
export const StyleGuideCardStates: React.FC = observer(function StyleGuideCardStates() {
  return (
    <div className={styles.section}>
      {/* ================================================================== */}
      {/* VERTICAL CARD STATES */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Vertical</h2>
      </div>

      {/* Image + Title — all states */}
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Image + Title</h3>
        <div className={styles.demo}>
          <div className={styles.stateGrid}>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>idle</span>
              <div className={styles.stateCardVerticalTall}>
                <StateCard label="Badge Scanner" />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>hover</span>
              <div className={`${styles.stateCardVerticalTall} ${styles.forceHover}`}>
                <StateCard label="Badge Scanner" hasCheck />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active</span>
              <div className={styles.stateCardVerticalTall}>
                <StateCard label="Badge Scanner" selected hasCheck />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>alwaysOn</span>
              <div className={styles.stateCardVerticalTall}>
                <StateCard label="Badge Scanner" selected disabled hasAlwaysOn />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>idle.disabled</span>
              <div className={styles.stateCardVerticalTall}>
                <StateCard label="Badge Scanner" disabled />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active.disabled</span>
              <div className={styles.stateCardVerticalTall}>
                <StateCard label="Badge Scanner" selected disabled />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image only (square) — states */}
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Image Only (Square)</h3>
        <div className={styles.demo}>
          <div className={styles.stateGrid}>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>idle</span>
              <div className={styles.stateCardVertical}>
                <Card orientation="vertical" imageSrc={IMG} value="swatch-idle" />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active</span>
              <div className={styles.stateCardVertical}>
                <Card orientation="vertical" imageSrc={IMG} value="swatch-active" selected hasCheck />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>alwaysOn</span>
              <div className={styles.stateCardVertical}>
                <Card orientation="vertical" imageSrc={IMG} value="swatch-always" selected disabled hasAlwaysOn />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>disabled</span>
              <div className={styles.stateCardVertical}>
                <Card orientation="vertical" imageSrc={IMG} value="swatch-disabled" disabled />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* HORIZONTAL CARD STATES */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Horizontal</h2>
      </div>

      {/* Horizontal: Full */}
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Image + Title + Description + Price</h3>
        <div className={styles.demo}>
          <div className={styles.stateGrid}>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>idle</span>
              <div className={styles.stateCardHorizontal}>
                <StateCard
                  label="Badge Scanner"
                  orientation="horizontal"
                  description="Effortlessly load your printer tray with access tickets (Z-fold) of various sizes."
                  cost="145,00 $"
                />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active</span>
              <div className={styles.stateCardHorizontal}>
                <StateCard
                  label="Badge Scanner"
                  orientation="horizontal"
                  description="Effortlessly load your printer tray with access tickets (Z-fold) of various sizes."
                  cost="145,00 $"
                  selected
                />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>alwaysOn</span>
              <div className={styles.stateCardHorizontal}>
                <StateCard
                  label="Badge Scanner"
                  orientation="horizontal"
                  description="Effortlessly load your printer tray with access tickets (Z-fold) of various sizes."
                  cost="145,00 $"
                  selected
                  disabled
                  hasAlwaysOn
                />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>disabled</span>
              <div className={styles.stateCardHorizontal}>
                <StateCard
                  label="Badge Scanner"
                  orientation="horizontal"
                  description="Effortlessly load your printer tray with access tickets (Z-fold) of various sizes."
                  cost="145,00 $"
                  disabled
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal: Compact */}
      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Title Only (Compact)</h3>
        <div className={styles.demo}>
          <div className={styles.stateGrid}>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>idle</span>
              <div className={styles.stateCardHorizontalCompact}>
                <Card orientation="horizontal" label="Badge Scanner" value="compact-idle" imageSrc={undefined} />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>active</span>
              <div className={styles.stateCardHorizontalCompact}>
                <Card
                  orientation="horizontal"
                  label="Badge Scanner"
                  value="compact-active"
                  selected
                  imageSrc={undefined}
                />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>alwaysOn</span>
              <div className={styles.stateCardHorizontalCompact}>
                <Card
                  orientation="horizontal"
                  label="Badge Scanner"
                  value="compact-always"
                  selected
                  disabled
                  hasAlwaysOn
                  imageSrc={undefined}
                />
              </div>
            </div>
            <div className={styles.stateItem}>
              <span className={styles.stateLabel}>disabled</span>
              <div className={styles.stateCardHorizontalCompact}>
                <Card
                  orientation="horizontal"
                  label="Badge Scanner"
                  value="compact-disabled"
                  disabled
                  imageSrc={undefined}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* EXTRAS: DESIGN TOKENS */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Card Design Tokens</h2>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Dimensions & Spacing</h3>
        <div className={styles.demo}>
          <table className={styles.tokenTable}>
            <thead>
              <tr>
                <th>Token</th>
                <th>CSS Variable</th>
                <th>Value</th>
                <th>Usage</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Card outer radius</td>
                <td>
                  <code>--radius-card-outers</code>
                </td>
                <td>16px (sm)</td>
                <td>Outer card border-radius</td>
              </tr>
              <tr>
                <td>Card inner radius</td>
                <td>
                  <code>--radius-card-inners</code>
                </td>
                <td>12px (sm)</td>
                <td>Inner content border-radius</td>
              </tr>
              <tr>
                <td>Image radius</td>
                <td>
                  <code>--radius-card-image-container</code>
                </td>
                <td>8px (sm)</td>
                <td>Image container border-radius</td>
              </tr>
              <tr>
                <td>Card padding</td>
                <td>
                  <code>--spacing-xs</code>
                </td>
                <td>4px</td>
                <td>Outer card padding</td>
              </tr>
              <tr>
                <td>Content gap</td>
                <td>
                  <code>--spacing-sm</code>
                </td>
                <td>8px</td>
                <td>Gap between image and text</td>
              </tr>
              <tr>
                <td>Grid gap</td>
                <td>
                  <code>--margin-list</code>
                </td>
                <td>4px</td>
                <td>Gap between cards in grid</td>
              </tr>
              <tr>
                <td>Min card size</td>
                <td>—</td>
                <td>96 x 96px</td>
                <td>Minimum card dimensions</td>
              </tr>
              <tr>
                <td>Image size</td>
                <td>—</td>
                <td>80 x 80px</td>
                <td>Card image placeholder</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>State Colors & Effects</h3>
        <div className={styles.demo}>
          <table className={styles.tokenTable}>
            <thead>
              <tr>
                <th>State</th>
                <th>Border</th>
                <th>Background</th>
                <th>Shadow</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Idle</td>
                <td>
                  <code>--color-grey-100</code>
                </td>
                <td>
                  <code>--color-elevated</code>
                </td>
                <td>none</td>
              </tr>
              <tr>
                <td>Hover</td>
                <td>
                  <code>--color-grey-100</code>
                </td>
                <td>
                  <code>--color-grey-50</code>
                </td>
                <td>none</td>
              </tr>
              <tr>
                <td>Active (selected)</td>
                <td>
                  <code>--color-brand-500-40</code>
                </td>
                <td>
                  <code>--color-grey-100</code>
                </td>
                <td>
                  <code>--shadow-button-primary</code>
                </td>
              </tr>
              <tr>
                <td>Focus</td>
                <td>—</td>
                <td>—</td>
                <td>
                  outline: 2px <code>--color-focus-40</code>
                </td>
              </tr>
              <tr>
                <td>Disabled (idle)</td>
                <td>
                  <code>--color-background</code>
                </td>
                <td>
                  <code>--color-background</code>
                </td>
                <td>none, opacity: 0.5</td>
              </tr>
              <tr>
                <td>AlwaysOn</td>
                <td>
                  <code>--color-brand-500-40</code>
                </td>
                <td>
                  <code>--color-grey-100</code>
                </td>
                <td>
                  <code>--shadow-button-primary</code> + lock icon
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ================================================================== */}
      {/* EXTRAS: ANIMATION */}
      {/* ================================================================== */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Card Click-Active Animation</h2>
      </div>

      <AnimationDemo />
    </div>
  );
});
